// src/tattoo/share.js
import { exportProjectHashAsync, importProjectHash } from "./state";
import { supabase } from "./lib/supabaseClient.js";

// --- cam encode/decode that tolerates old & new formats ---
export function encodeCamForShare(cam) {
  if (!cam) return null;
  if (typeof cam === "string") return cam; // already encoded
  if (typeof cam === "object") return encodeTinyUri(cam); // preferred
  return null;
}

export function decodeCamAny(x) {
  if (!x) return null;

  // If DB returns JSON object (jsonb column) just accept it
  if (typeof x === "object") return x;

  if (typeof x !== "string") return null;

  // 1) Preferred: url-safe b64 (encodeTinyUri)
  try {
    let b64 = x.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  } catch {}

  // 2) Old encodeTiny()
  try {
    return decodeTiny(x);
  } catch {}

  // 3) Your LayersPanel format: encodeURIComponent(btoa(JSON.stringify))
  try {
    const json = atob(decodeURIComponent(x));
    return JSON.parse(json);
  } catch {}

  return null;
}

// --- tiny camera encoder/decoder ---
export function encodeTiny(obj) {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
  } catch {
    return "";
  }
}
export function decodeTiny(b64) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch {
    return null;
  }
}

// stable viewer id
export function getViewerId() {
  try {
    let id = localStorage.getItem("tt_viewer_id");
    if (!id) {
      id = Math.random().toString(36).slice(2);
      localStorage.setItem("tt_viewer_id", id);
    }
    return id;
  } catch {
    return "anon";
  }
}

/**
 * Create a short share link: saves { payload, cam, focus, r } in Supabase,
 * returns URL with only "#s=<uuid>".
 */
export async function makeShortShareUrl({ payload, extras }) {
  try {
    const { cam, focus, r } = extras || {};
    const camStr = encodeCamForShare(cam);

    const { data, error } = await supabase
      .from("shares")
      .insert([{ payload, cam: camStr, focus: focus || null, r: !!r }])
      .select("id")
      .single();

    if (error || !data?.id) {
      const msg = error?.message || "insert failed";
      throw new Error(`[shares.insert] ${msg}`);
    }

    const url = new URL(window.location.href);
    url.search = "";
    url.hash = `s=${data.id}`;
    return url.toString();
  } catch (e) {
    console.warn("[share] makeShortShareUrl failed:", e);
    throw e; // <— bubble up the real reason
  }
}

/**
 * Check URL for a share, import project, and stash flags for the viewer to apply.
 * Call this once on boot (you already do that in main.jsx).
 */
export async function maybeLoadSharedProject() {
  try {
    const url = new URL(window.location.href);
    const hash = (url.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);

    // Prefer short share id
    const shareId = params.get("s");

    // legacy long-hash payload (we’ll still accept it, but try not to use it anymore)
    const payloadFromHash = params.get("p");

    let payload = null;
    let flags = { cam: null, focus: null, r: false };

    if (shareId) {
      // fetch the row from Supabase
      const { data, error } = await supabase
        .from("shares")
        .select("payload, cam, focus, r")
        .eq("id", shareId)
        .maybeSingle();
      if (error) {
        console.warn("[share] fetch by id failed:", error);
      }
      if (data) {
        payload = data.payload || null;
        flags.cam = data.cam ? decodeTinyUri(data.cam) : null;
        flags.focus = data.focus || null;
        flags.r = !!data.r;
      }
    } else if (payloadFromHash) {
      // legacy mode: read flags from hash too
      payload = payloadFromHash;
      const camB64 = params.get("cam");
      flags.cam = camB64 ? decodeTinyUri(camB64) : null;
      flags.focus = params.get("focus");
      flags.r = params.get("r") === "1";
    }

    if (!payload) {
      console.log("[share] no payload found");
      return false;
    }

    const ok = importProjectHash(payload);

    // ✅ apply garments/hair from saved project
    if (data?.appearance) {
      tattoo.appearance = {
        ...(tattoo.appearance || { top: "none", bottom: "none", hair: "none" }),
        ...data.appearance,
      };
    }
    console.log("[share] import ok?", !!ok);

    // Stash flags for PreviewPane to apply after camera/controls exist
    try {
      window.__tt_pending_share = {
        cam: flags.cam || null,
        focus: flags.focus || null,
        r: !!flags.r,
      };
      console.log(
        "[share] wrote window.__tt_pending_share",
        window.__tt_pending_share
      );
    } catch {}

    // Keep the URL *short*: retain only #s=<id> if present, else flags (optional)
    const clean = new URL(window.location.href);
    clean.search = "";
    if (shareId) {
      clean.hash = `s=${shareId}`;
    } else {
      const kept = new URLSearchParams();
      if (flags.r) kept.set("r", "1");
      if (flags.focus) kept.set("focus", flags.focus);
      if (flags.cam) kept.set("cam", encodeTiny(flags.cam));
      clean.hash = kept.toString();
    }
    history.replaceState({}, "", clean.toString());

    // Nudge morph UI (non-breaking if not used)
    try {
      window.dispatchEvent(new CustomEvent("morph:collect"));
      window.dispatchEvent(new CustomEvent("morph:changed"));
    } catch {}

    return !!ok;
  } catch (e) {
    console.warn("[share] maybeLoadSharedProject error:", e);
    return false;
  }
}
// --- Add to: src/tattoo/share.js ---

// URI-safe tiny encoder for small objects (e.g. camera)
export function encodeTinyUri(obj) {
  try {
    const json = JSON.stringify(obj);
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return "";
  }
}

export function decodeTinyUri(s) {
  try {
    if (!s) return null;

    // undo URL encoding if it happened (handles %3D and even %253D%253D cases)
    let v = s;
    for (let i = 0; i < 2; i++) {
      try {
        const d = decodeURIComponent(v);
        if (d === v) break;
        v = d;
      } catch {
        break;
      }
    }

    // urlsafe b64 -> normal b64
    v = v.replace(/-/g, "+").replace(/_/g, "/");

    // restore padding
    const pad = v.length % 4;
    if (pad) v += "=".repeat(4 - pad);

    const json = decodeURIComponent(escape(atob(v)));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Build a legacy viewer link that encodes everything in the hash (no network)
export function buildLegacyViewUrl({ payload, extras = {} }) {
  const { cam, focus, r } = extras;
  const base = new URL(window.location.origin + "/view");
  const qs = new URLSearchParams();
  // long payload in hash (works offline)
  if (payload) qs.set("p", payload);
  if (r) qs.set("r", "1");
  if (focus) qs.set("focus", String(focus));
  if (cam && typeof cam === "object") qs.set("cam", encodeTinyUri(cam));
  base.hash = qs.toString();
  return base.toString();
}

/**
 * Try short link via Supabase; if it fails (iPad quirks, network, etc.),
 * fall back to legacy hash URL so the user always gets a working link.
 */
export async function makeShareLinkResilient({ payload, extras = {} }) {
  try {
    // If caller gave us a raw cam object, make it tiny + URI-safe
    const camObj =
      extras.cam && typeof extras.cam === "object" ? extras.cam : null;

    const encodedExtras = {
      ...extras,
      cam: camObj ? encodeTinyUri(camObj) : extras.cam || null,
    };

    const short = await makeShortShareUrl({
      payload,
      extras: encodedExtras,
    });

    if (short) {
      const u = new URL(short);
      u.pathname = "/view";
      u.search = ""; // keep it clean

      // ✅ IMPORTANT: ensure hash contains s + extras (r/focus/cam)
      const h = new URLSearchParams(
        (u.hash || "").startsWith("#") ? u.hash.slice(1) : u.hash || ""
      );

      // If makeShortShareUrl returned `?s=...` or something else, preserve s if possible
      // but usually it already returns #s=UUID
      for (const [k, v] of Object.entries(encodedExtras)) {
        if (v == null) continue;
        h.set(k, String(v));
      }

      u.hash = "#" + h.toString();
      return u.toString();
    }
  } catch (e) {
    console.warn("[share] short link failed, falling back:", e);
  }

  return buildLegacyViewUrl({ payload, extras });
}

// src/tattoo/share.js

export async function getOrCreateShareUrlForCurrentProject(extras = {}) {
  // 1) serialize the current project
  const payload = await exportProjectHashAsync();
  if (!payload) {
    throw new Error("[share] No project payload to share");
  }

  // 2) read the current camera from PreviewPane (it exposes tt_getCam)
  let cam = null;
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.tt_getCam === "function"
    ) {
      cam = window.tt_getCam();
    }
  } catch (e) {
    console.warn(
      "[share] getOrCreateShareUrlForCurrentProject: tt_getCam failed",
      e
    );
  }

  // 3) combine extras (e.g. focus) with our defaults (read-only viewer)
  const finalExtras = {
    cam,
    r: true,
    ...extras,
  };

  // 4) Make a resilient /view link (short share if possible, else legacy hash)
  const url = await makeShareLinkResilient({
    payload,
    extras: finalExtras,
  });

  return url;
}
